import moment from "moment";
import _ from "lodash";
import uuidv4 from "uuid/v4";
import {
  cleanDate,
  cleanHour,
  createCalendarObj,
  isSessionSingleEvent,
  INITIAL_SESSION_STATE,
  getMonthsBetweenTwoDates,
  SLOT_MANIPULATION_OPTIONS,
  hydrateSessionSlotsWithAssignedPatients,
  getSessionStartHour,
  getSessionEndHour,
  APPOINTMENT_SESSION_MODE,
  calculateDuration,
  DEFAULT_SLOT_DURATION_IN_MINUTES,
} from '../../../utils/edReviewUtils';
import {SLOT_HEIGHT} from '../calendarView/dailyCalendar/edReviewCalendarDaily';
import {getIntervalRrule, getOccurrencesForSession} from './rruleUtils';
import {KIND_OF_REVIEW_STATE} from '../../../../../../../store/reducers/reviews';

const sessionIsLocked = (session) => !!session.locked;

////////////////////////////////////////////////////////
///// SESS INTERVAL CHECK  /////
//////////////////////////////////////////////////////

export const getSessionIntervalType = (session) => {
  let type = session?.intervalType;
  const isCustom = session?.customInterval;

  if (isCustom) {
    type = session?.customIntervalType;
  }

  switch (type) {
    case "day":
      return isCustom
        ? `Every ${session?.customIntervalNumber} Day`
        : "Every Day";
    case "week":
      return isCustom
        ? `Every ${session?.customIntervalNumber} Week`
        : "Every Week";
    case "2week":
      return isCustom
        ? `Every ${session?.customIntervalNumber} Two Weeks`
        : "Every Two Weeks";
    case "3week":
      return isCustom
        ? `Every ${session?.customIntervalNumber} Three Weeks`
        : "Every Three Weeks";
    case "4week":
      return isCustom
        ? `Every ${session?.customIntervalNumber} Four Weeks`
        : "Every Four Weeks";
    case "month":
      return isCustom
        ? `Every ${session?.customIntervalNumber} Month`
        : "Every Month";
    default:
      return "Every Week";
  }
};

const checkDefaultInterval = (session, selectedDay) => {
  const startDate = moment(session.startDate);

  switch (session?.intervalType) {
    case "week":
      return session;
    case "2week":
      return moment(selectedDay).diff(cleanHour(startDate), "weeks") % 2 === 0;
    case "3week":
      return moment(selectedDay).diff(startDate, "weeks") % 3 === 0;
    case "4week":
      return moment(selectedDay).diff(startDate, "weeks") % 4 === 0;
    default:
      return false;
  }
};

const checkIfSessionIsOnSelectedDayOrDayBeforeAfter = (
  session,
  selectedDay,
) => {
  const startDate = moment(session.startDate);
  const endDate = moment(session.endDate);

  if (
    (startDate.isBefore(selectedDay) || startDate.isSame(selectedDay)) &&
    (endDate.isSame(selectedDay) || endDate.isSame(selectedDay, "day", "next"))
  ) {
    return session;
  }
};

const getSessionsOnSelectedDay = (session, selectedDay) => {
  const startDate = moment(session.startDate);
  const endDate = moment(session.endDate);

  if (
    startDate.isSame(selectedDay, "day") &&
    endDate.isSame(selectedDay, "day")
  ) {
    return session;
  }
};

const checkCustomInterval = (session, selectedDay) => {
  const startDate = moment(session.startDate).set({ hours: 0, minutes: 0 });

  const selectedClean = moment(selectedDay).set({ hours: 0, minutes: 10 });
  switch (session?.customIntervalType) {
    case "day":
      return (
        getSessionsOnSelectedDay(session, selectedDay) ||
        moment(selectedClean).diff(startDate, "days") %
          parseInt(session.customIntervalNumber, 10) ===
          0
      );
    case "week":
      return (
        checkIfSessionIsOnSelectedDayOrDayBeforeAfter(session, selectedDay) ||
        moment(selectedDay).diff(startDate, "weeks") %
          parseInt(session.customIntervalNumber, 10) ===
          0
      );
    case "month":
      return (
        checkIfSessionIsOnSelectedDayOrDayBeforeAfter(session, selectedDay) ||
        moment(selectedDay).diff(startDate, "months") %
          parseInt(session.customIntervalNumber, 10) ===
          0
      );
    default:
      return false;
  }
};

const parseDefaultOption = (option) => {
  if (!option) return;

  const amount  = option.at(0);
  const optionType = option.slice(1);

  return [amount, optionType];
}

export const getMinMaxDate = (date, endDate = true, { intervalType, customInterval, customIntervalNumber, customIntervalType, daysOfWeek }) => {
  const startClone = moment(date);
  let extendedTime = moment(date);

  let parsedInterval;
  let firstOrLastSessionOnIsoDayOfWeek;

  if (!customInterval) {
    parsedInterval = parseDefaultOption(intervalType);
  } else {
    parsedInterval = [customIntervalNumber, customIntervalType];
  }

  if (!_.isEmpty(daysOfWeek)) {
    firstOrLastSessionOnIsoDayOfWeek = endDate ?
      _.last(daysOfWeek) :
      _.first(daysOfWeek);
  }

  if (parsedInterval) {
    extendedTime = endDate ?
      startClone.add(parsedInterval[0], parsedInterval[1]) :
      startClone.subtract(parsedInterval[0], parsedInterval[1]);
  }

  if (firstOrLastSessionOnIsoDayOfWeek) {
    if (endDate) {
      extendedTime.add(firstOrLastSessionOnIsoDayOfWeek - 1, 'days');
    } else {
      extendedTime.subtract(firstOrLastSessionOnIsoDayOfWeek - 1, 'days');
    }
  }

  return moment(extendedTime);
};

////////////////////////////////////////////////
///// SESSION RETRIEVE  /////
///////////////////////////////////////////////

export const getWardDataAppointmentsKey = (isBars, isSingleEvent) => {
  switch (isBars) {
    case true:
      return isSingleEvent ? 'singleBarsSessions' : 'barsList';
    case false:
    default:
      return isSingleEvent ? 'singleBedasSessions' : 'bedasList';
  }
}

const regularSessionIsBetweenStartAndEndDate = (session, date = moment()) => {
  return moment(date).isBetween(moment(session?.startDate), moment(session?.endDate), 'day', '[]');
}

export const getActiveSessionsByDay = (validActive, selectedDay) => {
  if (_.isEmpty(validActive)) return [];

  const repeatableSessions = _.filter(validActive, (session) => {
    if (!regularSessionIsBetweenStartAndEndDate(session, selectedDay)) {
      return false;
    }

    if (_.isEmpty(session?.daysOfWeek)) {
      if (session?.customInterval) {
        return (
          checkCustomInterval(session, selectedDay) ||
          getSessionsOnSelectedDay(session, selectedDay)
        );
      }
    }

    if (
      !_.isEmpty(session?.daysOfWeek) &&
      session?.daysOfWeek.includes(`${moment(selectedDay).isoWeekday()}`)
    ) {
      if (!session?.customInterval) {
        return (
          checkDefaultInterval(session, selectedDay) ||
          getSessionsOnSelectedDay(session, selectedDay)
        );
      } else {
        return checkCustomInterval(session, selectedDay);
      }
    }
  });

  return repeatableSessions;
};

export const getSessionValidWeekdays = (session) => {
  if (_.isEmpty(session?.daysOfWeek)) return "";
  let days = [];
  const clone = _.sortBy(_.cloneDeep(session?.daysOfWeek));

  clone.forEach((day, idx) => {
    days.push(moment().isoWeekday(parseInt(day, 10)).format("ddd"));
  });
  return days.join(", ");
};

////////////////////////////////////////////////
/////   SESSION SLOTS  /////
///////////////////////////////////////////////
export const createSessionSlotsObject = (sessionData, day, isSingleEvent = false) => {
  const isoDay = moment(day, "DD/MM/YYYY").isoWeekday();
  const slotDuration = getSessionSlotDuration(sessionData);
  let start = moment(cleanDate(getSessionStartHour(true, sessionData, isoDay, isSingleEvent)));
  let slotEnd = cleanDate(start).add(slotDuration, 'minutes');
  const sessionEnd = moment(cleanDate(getSessionEndHour(true, sessionData, isoDay, isSingleEvent)));
  const slots = {};

  if (isSingleEvent && !sessionData?.locked) {
    slots[start.format("HH:mm")] = null;
    return slots;
  }

  while (
    start.isBefore(sessionEnd, 'minute') &&
    !slotEnd.isAfter(sessionEnd, 'minute')
    ) {
    slots[start.format("HH:mm")] = null;
    start.add(slotDuration, "minutes");
    slotEnd.add(slotDuration, 'minutes');
  }

  if (sessionData?.fixedSlotDuration) {
    slots.slotDuration = getSessionSlotDuration(sessionData);
  }

  return slots;
}

export const getSessionSlots = (reviewsAssignedOnDate, slotObj, mode, sessionData, daySlots, activeContext) => {
  let slots = hydrateSessionSlotsWithAssignedPatients(reviewsAssignedOnDate, slotObj, sessionData, daySlots, activeContext);

  switch (mode) {
    case 'assigned':
      return _.pickBy(slots, s => !_.isNil(s) && s !== 'locked');
    case 'available':
      return _.pickBy(slots, _.isNil);
    default:
      return slots;
  }
}

const objectEmpty = (obj) => _.isEmpty(obj);

export const getSessionSlotDuration = (sessionObject) => sessionObject?.slotDuration || sessionObject?.slotDurationInMinutes || DEFAULT_SLOT_DURATION_IN_MINUTES;

////////////////////////////////////////////////
/////   DATA PREPARING  /////
///////////////////////////////////////////////
// TODO: Rework it later! First idea is to create arr with 'MM/YYYY'.
//  Then iterate over singleSessionArray and filter by month to let some breath to RAM.
export const prepareSessionsForCalendarObject = ({
  sessions,
  isBars = false,
  filterObject = null,
  currentCalendar = {},
  forceRefresh = false,
  watchedDate = moment(),
}) => {
  const { singleSessions, regularSessions } = sessions;
  let startDate, endDate;

  if (filterObject) {
    startDate = filterObject?.startDate || null;
    endDate = filterObject?.endDate || null;
  } else {
    startDate = moment(watchedDate).subtract(1, "months");
    endDate = moment(watchedDate).add(2, "months");
  }

  const filterStartDate = moment(startDate, "DD/MM/YYYY");
  const filterEndDate = moment(endDate || startDate, "DD/MM/YYYY");

  const sessionCalendar = _.cloneDeep(currentCalendar);
  const monthArr = getMonthsBetweenTwoDates(filterStartDate, filterEndDate);

  for (let month of monthArr) {
    if (sessionCalendar[month] && !forceRefresh) continue;

    const validMonth = moment(month, "MM/YYYY");
    sessionCalendar[month] = createCalendarObj(validMonth, "MM/YYYY");
  }

  if (isBars) {
    processBarsCalendar(sessionCalendar, sessions.regularSessions || []);
  } else {
    for (let month in sessionCalendar) {
      for (let day in sessionCalendar[month]) {
        if (!objectEmpty(sessionCalendar[month][day]) && !forceRefresh) continue;

        const sessionIdsForCurrentDay = { regular: [], single: [] };
        const dayOfMonth = moment(month, "MM/YYYY").set({ date: day });

        _.filter(singleSessions, (sess) =>
          moment(sess?.from || sess?.startDate).isSame(dayOfMonth, "day"),
        ).forEach((sess) => sessionIdsForCurrentDay.single.push(sess?.id || sess?.sessionId));

        getActiveSessionsByDay(regularSessions, dayOfMonth).forEach((sess) =>
          sessionIdsForCurrentDay.regular.push(sess?.sessionId),
        );

        sessionCalendar[month][day] = sessionIdsForCurrentDay;
      }
    }
  }

  return sessionCalendar;
};


const processBarsCalendar = (calendarClone, regularSessions) => {
  for (let month in calendarClone) {
    for (let day in calendarClone[month]) {
      const sessionIdsForCurrentDay = { regular: [], single: [] };
      const dayOfMonth = moment(month, "MM/YYYY").set({ date: day });

      regularSessions.forEach((sess) => {
        const occurrences = getOccurrencesForSession(
          sess,
          dayOfMonth.clone().startOf("day"),
          dayOfMonth.clone().endOf("day")
        );

        if (occurrences.length > 0) {
          sessionIdsForCurrentDay.regular.push(sess.sessionId);
        }
      });

      calendarClone[month][day] = sessionIdsForCurrentDay;
    }
  }

  return calendarClone;
};

export const prepareCurrentSessionForEditState = (session) => {
  let preparedObject = _.cloneDeep(INITIAL_SESSION_STATE);
  const isSingleEvent = isSessionSingleEvent(session);
  const isLocked = sessionIsLocked(session);
  const start = moment(session?.from || session?.startDate);
  const end = moment(session?.to || session?.endDate);
  const sessionName = session?.sessionName;

  preparedObject = {
    ...preparedObject,
    startTime: moment(cleanDate(start)),
    startDate: moment(cleanHour(start)),
    endTime: moment(cleanDate(end)),
    endDate: moment(cleanHour(end)),
    locked: isLocked,
    active: session?.active || true,
    sessionId: session?.sessionId,
  };

  if (sessionName) {
    preparedObject.sessionName = sessionName;
  }

  if (session.slotDurationInMinutes) {
    preparedObject.fixedSlotDuration = true;
    preparedObject.slotDurationInMinutes = session.slotDurationInMinutes;
  }

  if (session?.isBars) {
    preparedObject.isBars = session?.isBars;
    preparedObject.assignedSpecialty = session?.assignedSpecialty;
    preparedObject.healthCareService = session?.healthCareService;
    preparedObject.practitioner = session?.practitioner;
    preparedObject.practitionerRole = session?.practitionerRole;
  }

  //  Custom times ("HH:mm") are already UTC-formatted in db.
  if (!_.isEmpty(session?.customSessionTime)) {
    let customSessionTimes = {};

    Object.keys(session?.customSessionTime).forEach((key, index) => {
      customSessionTimes[key] = {
        startTime: moment(session?.customSessionTime[key]?.startTime, "HH:mm").toISOString(),
        endTime: moment(session?.customSessionTime[key]?.endTime, "HH:mm").toISOString(),
      }
    })

    preparedObject.customSessionTime = customSessionTimes;
  }

  const additionalFieldsToCheck = [
    'customInterval',
    'customIntervalNumber',
    'customIntervalType',
    'daysOfWeek',
    'intervalModeOn',
    'intervalType',
    'lockedReason',
    'omitDates',
  ]

  if (isSingleEvent) {
    preparedObject.singleEvent = true;
  } else {
    for (let additionalField of additionalFieldsToCheck) {
      if (_.isBoolean(session[additionalField]) || !!session[additionalField]) {
        preparedObject[additionalField] = session[additionalField];
      }
    }
  }

  return preparedObject;
};

// Create new sessions for each day in sessionState?.customSessionTime;
// New session creation draft
const getSessionStatus = (sessObj) => {
  if (sessObj.hasOwnProperty('status')) {
    return sessObj.status;
  } else {
    if (sessObj.active) return "active";
    if (sessObj.locked) return "locked";

    return '';
  }
}

export const isSessionActive = (sessObj) => {
  if (sessObj.hasOwnProperty('active')) {
    return !!sessObj.active;
  } else {
    return getSessionStatus(sessObj) === 'active';
  }
}

export const createBarsSessionObject = (sessionState) => {
  const startDate = moment(sessionState.startDate).set({
    hours: sessionState.startTime.hours(),
    minutes: sessionState.startTime.minutes(),
  }).utc();

  const endDate = moment(sessionState.endDate).set({
    hours: sessionState.endTime.hours(),
    minutes: sessionState.endTime.minutes(),
  }).utc();

  return ({
    sessionId: sessionState?.sessionId || sessionState?.id || uuidv4(),
    startDate: startDate.toDate(),
    endDate: endDate.toDate(),
    status: getSessionStatus(sessionState),
    sessionName: sessionState.sessionName || '',
    slotStartTime: startDate.format("HH:mm"),
    slotDurationInMinutes: getSessionSlotDuration(sessionState),
    healthCareService: sessionState.healthCareService,
    assignedSpecialty: sessionState.assignedSpecialty,
    rrule: getIntervalRrule({...sessionState, endDate: endDate.toISOString(), startDate: startDate.toISOString()}).toString(),
    practitioner: sessionState.practitioner,
    practitionerRole: sessionState.practitionerRole,
  })
}

export const createMultipleSessions = (sessionState, appointmentMode) => {
  const preparedSessions = [];
  const isBars = appointmentMode === KIND_OF_REVIEW_STATE.BARS;

  _.forEach(Object.keys(sessionState?.customSessionTime), isoWeekday => {
    const day = sessionState?.customSessionTime?.[isoWeekday];

    const tempSess = isBars ?
      createBarsSessionObject(sessionState) :
      createSessionObject(sessionState, appointmentMode);

    if (
      !cleanDate(moment(day.startTime)).isSame(cleanDate(sessionState?.startTime, 'minutes')) ||
      !cleanDate(moment(day.endTime)).isSame(cleanDate(sessionState?.endTime, 'minutes'))
    ) {

      if (isBars) {
        tempSess.startDate = moment(sessionState.startDate)
          .set({
            hours: moment(day.startTime).hours(),
            minutes: moment(day.startTime).minutes(),
          })
          .toDate();

        tempSess.endDate = moment(sessionState.endDate)
        .set({
          hours: moment(day.endTime).hours(),
          minutes: moment(day.endTime).minutes(),
        })
        .toDate();

        tempSess.slotStartTime = moment(tempSess.startDate).format("HH:mm");
      } else {
        tempSess.startDate = moment(sessionState.startDate)
          .set({
            hours: moment(day.startTime).hours(),
            minutes: moment(day.startTime).minutes(),
          })
          .toISOString();

        tempSess.endDate = moment(sessionState.endDate)
          .set({
            hours: moment(day.endTime).hours(),
            minutes: moment(day.endTime).minutes(),
          })
          .toISOString();

        tempSess.daysOfWeek = [isoWeekday];
      }

      tempSess.sessionId = uuidv4();

      tempSess.rrule = getIntervalRrule({
        ...sessionState,
        daysOfWeek: [isoWeekday],
        endDate: tempSess.endDate,
        startDate: tempSess.startDate
      }).toString();

      preparedSessions.push(tempSess)
    }
  })

  return preparedSessions;
}

const createSessionObject = (sessionState) => {
  let preparedSession = {
    sessionId: sessionState?.sessionId || sessionState?.id || uuidv4(),
    startDate: moment(sessionState.startDate)
      .set({
        hours: sessionState.startTime.hours(),
        minutes: sessionState.startTime.minutes(),
      })
      .toISOString(),
    endDate: moment(sessionState.endDate)
      .set({
        hours: sessionState.endTime.hours(),
        minutes: sessionState.endTime.minutes(),
      })
      .toISOString(),
    status: getSessionStatus(sessionState),
    active: sessionState?.active,
    locked: sessionState?.locked,
    slotDurationInMinutes: getSessionSlotDuration(sessionState),
  };

  if (!_.isEmpty(sessionState?.daysOfWeek)) {
    preparedSession.daysOfWeek = sessionState.daysOfWeek;
  }

  if (sessionState?.intervalModeOn) {
    preparedSession.rrule = getIntervalRrule(preparedSession).toString();
    preparedSession.intervalType = sessionState.intervalType;
    preparedSession.customInterval = sessionState.customInterval;
    preparedSession.customIntervalNumber = sessionState.customIntervalNumber;
    preparedSession.customIntervalType = sessionState.customIntervalType;
  } else {
    preparedSession.singleEvent = true;
  }

  if (sessionState?.locked) {
    preparedSession.lockedReason = sessionState.lockedReason;
  }

  if (sessionState?.sessionName) {
    preparedSession.sessionName = sessionState.sessionName;
  }

  preparedSession.isBars = false;

  return preparedSession;
}

const deleteFalsyValues = (sessionState) => {
  const sessionClone = _.cloneDeep(sessionState);

  for (let key in sessionClone) {
    if (_.isNil(sessionClone[key])) {
      delete sessionClone[key];
    }
  }

  return sessionClone;
}

export const prepareEdReviewSessionToApi = (sessionState, appointmentMode = APPOINTMENT_SESSION_MODE.ORDINARY) => {
  const preparedSessions = [];
  let preparedSession = null;

  if (!_.isEmpty(sessionState?.customSessionTime)) {
    preparedSessions.push(...createMultipleSessions(sessionState, appointmentMode))
  } else {
    preparedSession = createSessionObject(sessionState, appointmentMode);
  }

  if (!_.isEmpty(preparedSessions)) {
    let toSend = [];

    for (let session of preparedSessions) {
      toSend.push(deleteFalsyValues(session));
    }

    return toSend
  } else {
    return deleteFalsyValues(preparedSession);
  }
};

export const calculateTileHeight = (session) => {
  if (!session) {
    return SLOT_HEIGHT;
  } else if (session?.startDate && session?.endDate) {
    const duration = calculateDuration(session.startDate, session.endDate);

    return (duration / 30)  * SLOT_HEIGHT;
  } else {
    const duration = session?.fixedSlotDuration && session?.slotDurationInMinutes ? session?.slotDurationInMinutes : 30;

    return (duration / 30)  * SLOT_HEIGHT;
  }
}

export const updateMousePosition = (ev, sessionPaperRef, setCursorLocation) => {
  const timeline = document.getElementById("timeline-calendar");
  const rect = timeline.getBoundingClientRect();
  const tileRect = sessionPaperRef.current.getBoundingClientRect();
  const scrollY = window.scrollY || window.pageYOffset;

  const posX = ev.clientX - tileRect.x;

  const clientY = ev.clientY + scrollY;
  const posY = clientY - rect.top - scrollY;

  setCursorLocation({x: posX, y: posY});
};

////////////////////////////////////////////////
/////  LEFT CTX DAY CAL. /////
///////////////////////////////////////////////

export const isManipulatingSlots = (manipState) => (
  manipState?.isCreating ||
  manipState?.isLocking ||
  manipState?.isAssigning
);

export const getManipulationKind = (manipState) => {
  if (!!manipState?.isCreating) return SLOT_MANIPULATION_OPTIONS.CREATE_SLOT;
  if (!!manipState?.isLocking) return SLOT_MANIPULATION_OPTIONS.LOCK_SLOT;
  if (!!manipState?.isAssigning) return SLOT_MANIPULATION_OPTIONS.ASSIGN;

  return null;
}

export const prepareSingleSessions = ({ state, reason, lockedSessions }) => {
  const clone = _.cloneDeep(state);
  const preparedEvents = [];

  for (let sessionId in clone) {
    const tempAssignedSingleEvents = clone[sessionId];

    for (let ev of tempAssignedSingleEvents) {
      const preparedEv = {
        active: true,
        singleEvent: true,
        sessionId: uuidv4(),
        endDate: ev?.endDate,
        fixedSlotDuration: true,
        locked: !!lockedSessions,
        startDate: ev?.startDate,
        reason: ev?.lockedReason || "",
        sessionName: ev.sessionName || '',
        slotDuration: getSessionSlotDuration(ev),
      };

      if (ev?.isBars) {
        preparedEv.isBars = true;
      }

      if (!!lockedSessions && sessionId !== 'outsideSession') {
        preparedEv.parentSession = sessionId
      }

      preparedEvents.push(preparedEv);
    }
  }

  return preparedEvents;
};

export const changeBulkNewSlotKey = ({ key, value, sessionList }) => {
  if (!key) return sessionList || [];

  const clone = _.cloneDeep(sessionList);

  return clone.map((ev) => ({
    ...ev,
    [key]: value,
  }))
}

const isSlotFitsSessionBoundaries = (selectedHour, session) => {
  const selectedHrClone = moment(selectedHour);
  const slotDuration = getSessionSlotDuration(session);

  return selectedHour.isSameOrAfter(session?.startDate, 'minutes') &&
    !selectedHrClone.add(slotDuration, 'minutes').isAfter(session?.endDate, 'minutes');
}

export const canCreateNewLockedSession = (selectedHour, parentSession, manipulationState) => {
  if (!parentSession || !moment.isMoment(selectedHour)) {
    return false;
  }
  const { sessionId } = parentSession;
  const slotDuration = getSessionSlotDuration(parentSession);

  if (!isSlotFitsSessionBoundaries(selectedHour, parentSession)) {
    return ({type: 'error', message: 'Cannot create slot. Slot has to fit session boundaries.'});
  }

  if (!_.isEmpty(manipulationState?.[sessionId])) {
    for (let existingEvent of manipulationState?.[sessionId]) {
      const startDateTemp = getSessionStartHour(true, existingEvent, selectedHour.format("DD/MM/YYYY"), true);
      const endDateTemp = getSessionEndHour(true, existingEvent, selectedHour.format("DD/MM/YYYY"), true);

      if (moment(selectedHour).add(slotDuration, 'minutes').isBetween(startDateTemp, endDateTemp, 'minutes', '()')) {
        return ({type: 'error', message: 'Cannot create slot. Slot must not overlap another.'});
      }
    }
  }

  return true;
}

export const createNewLockedSession = (selectedHour, parentSession) => {
  if (!moment.isMoment(selectedHour)) {
    return null;
  }

  const slotDuration = getSessionSlotDuration(parentSession);

  return {
    sessionId: uuidv4(),
    slotDuration,
    active: true,
    locked: true,
    sessionName: '',
    lockedReason: "",
    singleEvent: true,
    fixedSlotDuration: true,
    startDate: selectedHour.toISOString(),
    parentSession: parentSession?.sessionId,
    endDate: moment(selectedHour).add(slotDuration, 'minutes').toISOString(),
  }
}

export const canCreateNewSingleSession = (selectedHour, parentSession, manipulationState, columnIndex) => {
  if (!parentSession || !moment.isMoment(selectedHour)) {
    return false;
  }

  const slotDuration = getSessionSlotDuration();
  const eventsClone = _.cloneDeep(manipulationState?.[columnIndex] || []);
  const sortedEvents = eventsClone.sort((ev) => moment(ev?.startDate).toISOString());

  if (!_.isEmpty(sortedEvents)) {
    for (let existingEvent of sortedEvents) {
      const startDateTemp = getSessionStartHour(true, existingEvent, selectedHour.format("DD/MM/YYYY"), existingEvent?.singleEvent);
      const endDateTemp = getSessionEndHour(true, existingEvent, selectedHour.format("DD/MM/YYYY"), existingEvent?.singleEvent);

      if (moment(selectedHour).add(slotDuration, 'minutes').isBetween(startDateTemp, endDateTemp, 'minutes', '()')) {
        return ({type: 'error', message: 'Cannot create slot. Slot must not overlap another.'});
      }
    }
  }

  return true;
}

export const createNewSingleSession = (selectedHour, parentSession) => {
  if (!moment.isMoment(selectedHour)) {
    return null;
  }

  const slotDuration = getSessionSlotDuration(parentSession);

  return ({
    sessionId: uuidv4(),
    slotDuration,
    active: true,
    locked: false,
    sessionName: '',
    singleEvent: true,
    parentSession: 'outsideSession',
    fixedSlotDuration: true,
    startDate: selectedHour.toISOString(),
    endDate: moment(selectedHour).add(slotDuration, 'minutes').toISOString(),
  })
}